/*----------------------------------
- MOBILE MENU SECTION
----------------------------------*/

import type { ComponentChildren } from 'preact';

import Icon from '@/client/components/ui/icon/Icon';

/*----------------------------------
- TYPES
----------------------------------*/

type MobileMenuSectionProps = {
    id: string;
    label: string;
    isOpen: boolean;
    onToggle: () => void;
    testId: string;
    /** The open body's own test id, when a journey reads the section's content as one block (Domains). */
    bodyTestId?: string;
    children: ComponentChildren;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

/**
 * One in-place disclosure of the phone drawer (`.msm-sec`: the row title at the heading size, its chevron in a round
 * button that turns ink and points up while open, the body). A button with `aria-expanded` rather than the kit
 * `Disclosure` (`<details>`): the drawer opens one section at a time, which the browser's own `<details>` state cannot
 * enforce. The body unfolds in place (`.msm-sec.on > .msm-body`, grid rows 0fr to 1fr) and is folded by `visibility`, not
 * `hidden` and never unmounted: every link it holds stays in the server-rendered document for crawlers, while a folded
 * body is out of the Tab order and the accessibility tree, and the height can animate (`.msm-in` clips it).
 */
export default function MobileMenuSection({ id, label, isOpen, onToggle, testId, bodyTestId, children }: MobileMenuSectionProps) {
    return (
        <div class={isOpen ? 'msm-sec on' : 'msm-sec'}>
            <button type="button" onClick={onToggle} aria-expanded={isOpen} aria-controls={id} data-testid={testId} class="msm-row">
                <b>{label}</b>
                <span class={isOpen ? 'msm-g ud-icon-btn ud-icon-btn--ink' : 'msm-g ud-icon-btn'} aria-hidden="true">
                    <Icon name="chevron-down" size={18} strokeWidth={2} />
                </span>
            </button>
            <div id={id} class="msm-body" data-testid={bodyTestId}>
                <div class="msm-in">{children}</div>
            </div>
        </div>
    );
}
